import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, Bell, ChevronDown } from "lucide-react";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import type { InstantNotificationCategory } from "@/features/user-profile/types";
import {
  createInstantNotificationSchema,
  type InstantNotificationFormValues,
} from "@/features/user-setting/schema";
import { useInstantNotificationSettings } from "@/hooks/useInstantNotificationSettings";
import { useNotificationSpacesByCategory } from "@/hooks/useNotificationSpacesByCategory";
import { useToast } from "@/hooks/useToast";

interface InstantNotificationTabProps {
  onClose: () => void;
}

interface CategoryHeaderProps {
  category: InstantNotificationCategory;
  checked: boolean;
  isSaving: boolean;
  onChange: (checked: boolean) => void;
  getCategoryTitle: (categoryId: string, fallback: string) => string;
  getCategoryDescription: (categoryId: string, fallback: string) => string;
}

const CategoryHeader: React.FC<CategoryHeaderProps> = ({
  category,
  checked,
  isSaving,
  onChange,
  getCategoryTitle,
  getCategoryDescription,
}) => (
  <div className="flex items-start justify-between space-x-4">
    <div className="flex-1 space-y-1">
      <div className="flex items-center gap-2">
        <Bell className="size-4 text-gray-500" />
        <FormLabel
          className="cursor-pointer text-sm font-medium"
          htmlFor={category.id}
        >
          {getCategoryTitle(category.id, category.title)}
        </FormLabel>
      </div>
      <p className="ml-6 text-sm text-gray-600">
        {getCategoryDescription(category.id, category.description)}
      </p>
    </div>
    <FormControl>
      <Switch
        id={category.id}
        checked={checked}
        disabled={category.is_fixed || isSaving}
        onCheckedChange={onChange}
      />
    </FormControl>
  </div>
);

interface SpaceSettingsPanelProps {
  categoryId: string;
  expanded: boolean;
  loading: boolean;
  items: Array<{
    space_id: number;
    space_name: string;
    available: boolean;
    enabled: boolean;
  }>;
  isSaving: boolean;
  isSavingSpaceSettings: boolean;
  updatingKeys: Set<string>;
  pendingSpaceSettings: Record<string, Record<number, boolean>>;
  onToggleExpanded: (categoryId: string) => void;
  onPendingChange: (
    categoryId: string,
    spaceId: number,
    enabled: boolean,
  ) => void;
  t: (key: string) => string;
}

const SpaceSettingsPanel: React.FC<SpaceSettingsPanelProps> = ({
  categoryId,
  expanded,
  loading,
  items,
  isSaving,
  isSavingSpaceSettings,
  updatingKeys,
  pendingSpaceSettings,
  onToggleExpanded,
  onPendingChange,
  t,
}) => (
  <div
    className={`ml-6 ${expanded ? "rounded-md border bg-gray-50 p-3" : "py-1"}`}
  >
    <button
      type="button"
      onClick={() => onToggleExpanded(categoryId)}
      className="flex w-full items-center justify-between rounded-md p-1 text-left transition-colors hover:bg-black/5"
    >
      <div className="ml-3 text-sm text-gray-500">
        {t("common.studyGroupNotificationSettings")}
      </div>
      <div
        className={`flex items-center text-sm ${expanded ? "text-gray-600" : "text-gray-400"}`}
      >
        <ChevronDown
          className={`size-4 transition-transform ${expanded ? "rotate-180" : ""}`}
        />
      </div>
    </button>

    {expanded && (
      <div>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <div className="size-3 animate-spin rounded-full border-b-2 border-gray-400" />
            <span>{t("common.loading")}</span>
          </div>
        ) : items.length ? (
          <div className="space-y-2">
            {items.map((item) => {
              const key = `${categoryId}:${item.space_id}`;
              const isUpdating = updatingKeys.has(key);
              const pendingValue =
                pendingSpaceSettings[categoryId]?.[item.space_id];
              const checkedValue = pendingValue ?? item.enabled;
              return (
                <div
                  key={item.space_id}
                  className="flex items-center justify-between gap-3 rounded-md bg-white px-3 py-2"
                >
                  <div className="text-sm text-text-subtitle">
                    {item.space_name}
                  </div>
                  <Switch
                    id={`group-${categoryId}-${item.space_id}`}
                    checked={checkedValue}
                    disabled={
                      !item.available ||
                      isSaving ||
                      isSavingSpaceSettings ||
                      isUpdating
                    }
                    onCheckedChange={(checked) =>
                      onPendingChange(categoryId, item.space_id, checked)
                    }
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-sm text-gray-500">
            {t("common.noStudyGroupsAvailable")}
          </div>
        )}
      </div>
    )}
  </div>
);

export const InstantNotificationTab: React.FC<InstantNotificationTabProps> = ({
  onClose,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const {
    instantNotificationSettings,
    isLoading,
    isSaving,
    saveSettings,
    submitError,
  } = useInstantNotificationSettings();
  const [expandedCategories, setExpandedCategories] = useState<
    Record<string, boolean>
  >({});
  const [pendingSpaceSettings, setPendingSpaceSettings] = useState<
    Record<string, Record<number, boolean>>
  >({});
  const [isSavingSpaceSettings, setIsSavingSpaceSettings] = useState(false);

  // 表示対象のカテゴリIDを定義
  const allowedCategoryIds = [
    "mail",
    "mail_conversation",
    "calendar",
    "mentioned",
    "comments",
    "space_member",
  ];

  const groupSettingCategoryIds = [
    "calendar",
    "mentioned",
    "comments",
    "space_member",
  ];

  // 表示対象のカテゴリのみをフィルタリング
  const filteredCategories = useMemo(() => {
    const list =
      instantNotificationSettings?.categories?.filter((category) =>
        allowedCategoryIds.includes(category.id),
      ) || [];

    const order = [
      "calendar",
      "mentioned",
      "comments",
      "space_member",
      "mail_conversation",
      "mail",
    ];

    return [...list].sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
  }, [instantNotificationSettings?.categories]);

  const getCategoryTitle = (categoryId: string, fallback: string) => {
    return t(`common.notificationCategories.${categoryId}.title`, {
      defaultValue: fallback,
    });
  };

  const getCategoryDescription = (categoryId: string, fallback: string) => {
    return t(`common.notificationCategories.${categoryId}.description`, {
      defaultValue: fallback,
    });
  };

  const form = useForm<InstantNotificationFormValues>({
    resolver: zodResolver(createInstantNotificationSchema()),
    defaultValues: { notifications: {} },
    mode: "onChange",
  });

  const watchedNotifications = useWatch({
    control: form.control,
    name: "notifications",
  });

  const enabledByCategory = useMemo(
    () =>
      groupSettingCategoryIds.reduce(
        (acc, categoryId) => {
          const category = filteredCategories.find(
            (item) => item.id === categoryId,
          );
          if (!category) return acc;
          acc[categoryId] =
            watchedNotifications?.[categoryId] ?? category.default_setting;
          return acc;
        },
        {} as Record<string, boolean>,
      ),
    [filteredCategories, watchedNotifications],
  );

  const {
    dataByCategory,
    loadingByCategory,
    updateSpaceSetting,
    updatingKeys,
  } = useNotificationSpacesByCategory(
    groupSettingCategoryIds.filter((categoryId) =>
      filteredCategories.some((category) => category.id === categoryId),
    ),
    enabledByCategory,
  );

  const hasPendingSpaceSettings = useMemo(
    () =>
      Object.values(pendingSpaceSettings).some(
        (bySpace) => Object.keys(bySpace).length > 0,
      ),
    [pendingSpaceSettings],
  );

  const toggleCategoryExpanded = (categoryId: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const updatePendingSpaceSetting = (
    categoryId: string,
    spaceId: number,
    enabled: boolean,
  ) => {
    setPendingSpaceSettings((prev) => ({
      ...prev,
      [categoryId]: {
        ...(prev[categoryId] || {}),
        [spaceId]: enabled,
      },
    }));
  };

  const buildFailedSpaceLabels = (
    failedEntries: Array<{ categoryId: string; spaceId: number }>,
  ): string =>
    failedEntries
      .map((entry) => {
        const categoryLabel = getCategoryTitle(
          entry.categoryId,
          entry.categoryId,
        );
        const spaceName =
          dataByCategory[entry.categoryId]?.find(
            (item) => item.space_id === entry.spaceId,
          )?.space_name ?? `#${entry.spaceId}`;
        return `${categoryLabel} / ${spaceName}`;
      })
      .join(", ");

  const saveSpaceSettings = async () => {
    if (!hasPendingSpaceSettings) return;

    setIsSavingSpaceSettings(true);
    const pendingEntries = Object.entries(pendingSpaceSettings).flatMap(
      ([categoryId, bySpace]) =>
        Object.entries(bySpace).map(([spaceId, enabled]) => ({
          categoryId,
          spaceId: Number(spaceId),
          enabled,
        })),
    );

    const results = await Promise.allSettled(
      pendingEntries.map((entry) =>
        updateSpaceSetting(entry.spaceId, entry.categoryId, entry.enabled),
      ),
    );

    const failedEntries = results
      .map((result, index) => ({ result, entry: pendingEntries[index] }))
      .filter(({ result }) => result.status === "rejected")
      .map(({ entry }) => entry);

    setPendingSpaceSettings((prev) => {
      const next = { ...prev };
      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          const { categoryId, spaceId } = pendingEntries[index];
          if (next[categoryId]) {
            const { [spaceId]: _removed, ...rest } = next[categoryId];
            next[categoryId] = rest;
            if (Object.keys(next[categoryId]).length === 0) {
              delete next[categoryId];
            }
          }
        }
      });
      return next;
    });

    if (failedEntries.length > 0) {
      const failedLabels = buildFailedSpaceLabels(failedEntries);
      toast({
        title: t("common.settingsError"),
        description:
          failedEntries.length === 1
            ? t("common.notificationSettingSaveFailed", {
                defaultValue: "Failed to save: {{target}}",
                target: failedLabels,
              })
            : t("common.notificationSettingSaveFailedMultiple", {
                defaultValue: "Failed to save some settings: {{targets}}",
                targets: failedLabels,
              }),
      });
    }

    setIsSavingSpaceSettings(false);
  };

  useEffect(() => {
    if (!instantNotificationSettings) return;

    const defaults = filteredCategories.reduce(
      (acc, category) => {
        const currentSetting =
          instantNotificationSettings.settings[category.id];
        acc[category.id] = currentSetting?.enabled ?? category.default_setting;
        return acc;
      },
      {} as Record<string, boolean>,
    );

    form.reset({ notifications: defaults });
    setExpandedCategories(
      filteredCategories.reduce(
        (acc, category) => {
          acc[category.id] = false;
          return acc;
        },
        {} as Record<string, boolean>,
      ),
    );
    setPendingSpaceSettings({});
  }, [instantNotificationSettings, filteredCategories, form]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="size-8 animate-spin rounded-full border-b-2 border-teal-500" />
      </div>
    );
  }

  if (!instantNotificationSettings) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-red-500">{t("common.errorLoading")}</div>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(async (values) => {
          await saveSettings(values.notifications);
          await saveSpaceSettings();
          form.reset(values);
        })}
        className="space-y-6"
      >
        <div className="mb-4">
          <h3 className="text-lg font-medium">
            {t("common.notificationSettings")}
          </h3>
          <p className="text-sm text-gray-500">
            {t("common.notificationSettingsDesc")}
          </p>
        </div>

        {submitError && (
          <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">
            <AlertCircle className="size-4" />
            <span className="text-sm">{submitError}</span>
          </div>
        )}

        <div className="space-y-6">
          {filteredCategories.length > 0 ? (
            filteredCategories.map((category) => (
              <FormField
                key={category.id}
                control={form.control}
                name={`notifications.${category.id}` as const}
                render={({ field }) => (
                  <FormItem className="space-y-3">
                    <CategoryHeader
                      category={category}
                      checked={field.value ?? category.default_setting}
                      isSaving={isSaving}
                      onChange={(checked) => field.onChange(checked)}
                      getCategoryTitle={getCategoryTitle}
                      getCategoryDescription={getCategoryDescription}
                    />

                    {groupSettingCategoryIds.includes(category.id) &&
                      (field.value ?? category.default_setting) && (
                        <SpaceSettingsPanel
                          categoryId={category.id}
                          expanded={expandedCategories[category.id]}
                          loading={loadingByCategory[category.id]}
                          items={dataByCategory[category.id] ?? []}
                          isSaving={isSaving}
                          isSavingSpaceSettings={isSavingSpaceSettings}
                          updatingKeys={updatingKeys}
                          pendingSpaceSettings={pendingSpaceSettings}
                          onToggleExpanded={toggleCategoryExpanded}
                          onPendingChange={updatePendingSpaceSetting}
                          t={t}
                        />
                      )}
                  </FormItem>
                )}
              />
            ))
          ) : (
            <div className="py-4 text-center text-gray-500">
              {t("common.noNotificationCategoriesAvailable")}
            </div>
          )}
        </div>

        <div className="-mx-6 -mb-6 flex justify-end gap-3 border-t bg-gray-50 p-6">
          <Button type="button" variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            variant="teal"
            disabled={
              isSaving ||
              isSavingSpaceSettings ||
              (!form.formState.isDirty && !hasPendingSpaceSettings)
            }
          >
            {isSaving || isSavingSpaceSettings
              ? t("common.saving")
              : t("common.save")}
          </Button>
        </div>
      </form>
    </Form>
  );
};
